import {
    RiArticleLine,
    RiCloseCircleFill, RiCloseCircleLine, RiExternalLinkLine, RiEyeLine, RiListCheck,
    RiQuestionAnswerLine,
    RiSendPlaneLine,
    RiSparklingLine,
} from "@remixicon/react";
import React, {
    useCallback, useEffect, useMemo, useState,
} from "react";
import {
    Article,
    ArticleType,
    GetArticleDocument,
    GetArticlesByTagDocument,
    ListArticleVersionsDocument,
    ListProposalArticlesDocument,
    useDeleteArticleVersionMutation,
    useGetArticleQuery,
    useListProposalArticlesQuery,
    usePublishArticleMutation,
    useUpdateArticleTypeMutation,
} from "../../../../__generated__/api";
import client from "../../../../api";
import { useConfirm } from "../../../../components/confirm/Confirm";
import { useDeleteConfirmWithBody } from "../../../../components/confirm/Delete";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import Badge from "../../../../components/theme/badge/Badge";
import Button from "../../../../components/theme/button/Button";
import Icon from "../../../../components/theme/icon/Icon";
import SettingsContainer from "../../../../components/theme/settings";
import createTableContainer from "../../../../components/theme/table";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import { Paths } from "../../../../contexts/Routing";
import { tryParseDate } from "../../../../date";
import ArticleView from "../Article";
import { PublishItem, PublishType } from "../Publish";
import Styles from "./Proposals.module.scss";

const TableContainer = createTableContainer<ArticleWithStatus>();

enum ArticleStatus {
    PROPOSED_NEW = "PROPOSED_NEW",
    PROPOSED_CHANGED = "PROPOSED_CHANGED",
}

interface ArticleWithStatus extends Article {
    status: ArticleStatus;
}

const STATUS_BADGE = {
    [ArticleStatus.PROPOSED_NEW]: <Badge variant="success">New</Badge>,
    [ArticleStatus.PROPOSED_CHANGED]: <Badge variant="info">Changed</Badge>,
};

function useArticlePreview() {
    const [previewArticle, setPreviewArticle] = useState<Article | null>(null);

    const { data, loading: isLoading } = useGetArticleQuery({
        skip: !previewArticle,
        variables: {
            id: previewArticle?.id,
            versionId: previewArticle?.versionId,
        },
    });

    const [deleteVersion] = useDeleteArticleVersionMutation();

    const rejectVersion = useCallback(async () => {
        await deleteVersion({
            refetchQueries: [
                {
                    query: ListProposalArticlesDocument,
                },
                {
                    query: ListArticleVersionsDocument,
                    variables: {
                        id: previewArticle?.id,
                    },
                },
            ],
            variables: {
                id: previewArticle?.versionId,
            },
        });
        setPreviewArticle(null);
    }, [deleteVersion, previewArticle]);

    const current = useMemo(() => (data?.article ?? previewArticle) as Article, [data?.article, previewArticle]);

    const { element, setShouldShow: setShouldShowPreview } = useConfirm({
        title: current?.title,
        body: current
            ? (
                <ArticleView
                    isLoading={isLoading}
                    isMinimalActions
                    article={current}
                    canPreview={false}
                    canPublishKnowledge={previewArticle?.type !== ArticleType.Knowledge}
                    actionButtons={(
                        <Button
                            prefixIcon={<Icon icon={<RiCloseCircleLine />} />}
                            variant="danger"
                            size="small"
                            onClick={rejectVersion}
                        >
                            Reject
                        </Button>
                    )}
                    onPublish={type => LoggingHandler.instance.wrap(async () => {
                        const include = [
                            ListProposalArticlesDocument,
                        ];
                        if (type !== PublishType.KNOWLEDGE) {
                            include.push(GetArticlesByTagDocument);
                        }
                        await client.refetchQueries({
                            include,
                        });
                        setPreviewArticle(null);
                        LoggingHandler.instance.metric([
                            LoggingHandler.Metrics.ARTICLES, "proposals", "publish", type.toLowerCase(),
                        ]);
                    })}
                />
            )
            : undefined,
        size: "xl",
        shouldShowFooter: false,
        bodyClassName: Styles["preview-wrapper"],
        rootClassName: Styles["publish-modal"],

    });

    useEffect(() => {
        if (!previewArticle) {
            setShouldShowPreview(false);
        }
    }, [previewArticle]);

    return {
        element,
        showPreview: (preview: Article) => {
            setShouldShowPreview(true);
            setPreviewArticle(preview);
        },
    };
}

export default function Proposed() {
    const { data, error, loading: isLoading } = useListProposalArticlesQuery();

    const proposals = useMemo(
        () => data?.knowledge.results.filter(row => row.proposal)
            .concat(data?.proposals.results)
            .map(row => ({
                ...row,
                status: row.type === ArticleType.Knowledge ? ArticleStatus.PROPOSED_NEW : ArticleStatus.PROPOSED_CHANGED,
            }))
            .dedupe(row => row.id, (left, right) => (left.modifiedDate > right.modifiedDate ? left : right)),
        [data],
    );

    const { properties } = useFormBuilder(
        builder => builder
            .property("selectedIds", () => new Set<string>())
            .property("publishDate", () => undefined as Date | undefined)
            .property("publishType", () => ArticleType.Article)
            .property<"previewArticle", Article | null>("previewArticle", () => null),
        [],
    );

    const [deleteVersion] = useDeleteArticleVersionMutation();

    const { element: deleteElement, setDeleting: setRejected } = useDeleteConfirmWithBody<Article[]>(
        async items => {
            await items.forEachConcurrent(async item => {
                await deleteVersion({
                    refetchQueries: [
                        {
                            query: ListArticleVersionsDocument,
                            variables: {
                                id: item?.id,
                            },
                        },
                    ],
                    variables: {
                        id: item?.versionId,
                    },
                });
            });
            client.refetchQueries({
                include: [
                    ListProposalArticlesDocument,
                ],
            });
            properties.selectedIds.setValue(new Set());
        },
        "Reject Articles",
        (
            <>
                Do you want to reject
                {" "}
                {properties.selectedIds.value.size}
                {" "}
                selected article
                {properties.selectedIds.value.size > 1 ? "s" : ""}
                ?
            </>
        ),
        "Reject",
    );

    const [publish] = usePublishArticleMutation();

    const { element: publishElement, setShouldShow: setShowPublish } = useConfirm({
        title: "Publish Article",
        confirmText: "Publish",
        closeText: "Cancel",
        bodyClassName: Styles["publish-wrapper"],
        onConfirm: async () => {
            const publishDate = properties.publishDate.value;
            const articleType = properties.publishType.value;
            const items = Array.from(properties.selectedIds.value).map(id => proposals?.toIdMap("id")?.[id] as ArticleWithStatus);
            await items.forEachConcurrent(async item => {
                await publish({
                    refetchQueries: [
                        { query: GetArticleDocument, variables: { id: item?.id } },
                        { query: ListArticleVersionsDocument, variables: { id: item?.id } },
                    ],
                    variables: {
                        id: item?.id,
                        settings: publishDate || articleType === ArticleType.Knowledge ? {
                            publishDate,
                            type: articleType,
                        } : undefined,
                    },
                });
            });
            client.refetchQueries({
                include: [
                    ListProposalArticlesDocument,
                ],
            });
            properties.selectedIds.setValue(new Set());

            LoggingHandler.instance.metric([
                LoggingHandler.Metrics.ARTICLES, "proposals", "bulk-publish", articleType.toLowerCase(),
            ]);
        },
        body: (
            <PublishItem
                onChange={(type, date) => {
                    properties.publishDate.setValue(date);
                    properties.publishType.setValue(type === PublishType.KNOWLEDGE ? ArticleType.Knowledge : ArticleType.Article);
                }}
                canPublishKnowledge
            />
        ),
    });

    const { element, showPreview } = useArticlePreview();

    return (
        <div className={Styles["proposed-container"]}>
            {deleteElement}
            {publishElement}
            <SettingsContainer navigation="tabs">
                <SettingsContainer.Section title="Proposed Articles" shouldDisplayTitle={false}>
                    <SettingsContainer.Setting
                        title="Proposals"
                        description="."
                        permissions={SettingsContainer.Setting.Permissions.Everyone}
                        isFullWidth
                    >
                        <TableContainer data={proposals as ArticleWithStatus[] ?? []} isLoading={isLoading} error={error}>
                            <TableContainer.Empty>
                                <TableContainer.Empty.Default
                                    text="You dont have any proposed article changes. Stay tuned,
                                        we will notify you when we have something to review."
                                    title="Proposed Changes"
                                    icon={<RiSparklingLine />}
                                />
                            </TableContainer.Empty>

                            <TableContainer.TableControls>

                                <TableContainer.TableControls.Search
                                    search={(query, item) => item.title?.toLocaleLowerCase().includes(query)
                                            || item.body.plaintext?.toLocaleLowerCase().includes(query)
                                            || item.type?.toLocaleLowerCase().includes(query)}
                                />

                                {properties.selectedIds.value.size > 0 ? (
                                    <>
                                        <TableContainer.TableControls.Button
                                            variant="outline-blue"
                                            postfixIcon={<RiCloseCircleFill />}
                                            onClick={() => properties.selectedIds.setValue(new Set())}
                                            size="small"
                                            className={Styles["button-clear"]}
                                            text={`${properties.selectedIds.value.size} selected`}
                                        />
                                        <TableContainer.TableControls.Seperator />
                                        <TableContainer.TableControls.Button
                                            variant="primary"
                                            prefixIcon={<RiSendPlaneLine />}
                                            onClick={() => setShowPublish(true)}
                                            size="small"
                                            text="Publish"
                                        />
                                        <TableContainer.TableControls.Button
                                            variant="danger"
                                            prefixIcon={<RiCloseCircleLine />}
                                            onClick={() => {
                                                const articleMap = proposals?.toIdMap("id") ?? {};
                                                const selected = Array
                                                    .from(properties.selectedIds.value)
                                                    .map(id => articleMap[id] as ArticleWithStatus);
                                                setRejected(selected);
                                            }}
                                            size="small"
                                            text="Reject"
                                        />
                                    </>

                                ) : (
                                    <>
                                        <TableContainer.TableControls.Seperator />
                                        <TableContainer.TableControls.SortBy />
                                    </>
                                )}

                            </TableContainer.TableControls>
                            <TableContainer.Table>
                                <TableContainer.RowSelector
                                    selection={properties.selectedIds.value}
                                    onSelectionChange={selection => {
                                        properties.selectedIds.setValue(selection);
                                    }}
                                />

                                <TableContainer.TableValue
                                    columnKey="title"
                                    title="Title"
                                    sortBy={item => item.title?.toLowerCase()}
                                >
                                    {item => item.title}
                                </TableContainer.TableValue>
                                <TableContainer.TableValue
                                    columnKey="date"
                                    title="Date"
                                    sortBy={item => item.modifiedDate}
                                >
                                    {item => tryParseDate(item.modifiedDate)?.toDateString()}
                                </TableContainer.TableValue>
                                <TableContainer.TableValue
                                    columnKey="ty"
                                    title="Type"
                                    sortBy={item => (item.status)}
                                >
                                    {item => STATUS_BADGE[item.status ?? ArticleStatus.PROPOSED_NEW]}
                                </TableContainer.TableValue>

                                <TableContainer.TableValue
                                    columnKey="score"
                                    title="Score"
                                    sortBy={item => item.proposal?.score}
                                >
                                    {item => item.proposal?.score}
                                </TableContainer.TableValue>

                                <TableContainer.Action
                                    icon={<RiEyeLine />}
                                    altText="Preview Article"
                                    onClick={showPreview}
                                    metricPath={[
                                        LoggingHandler.Metrics.ARTICLES, "proposals", "article-expand",
                                    ]}
                                />
                                <TableContainer.Action
                                    icon={<RiExternalLinkLine />}
                                    altText="Open Article"
                                    isNewTab
                                    to={Paths.ANSWERS_GET_VERSION}
                                    getLinkParams={item => ({ id: item.id, versionId: item.proposal?.versionId })}
                                    metricPath={[
                                        LoggingHandler.Metrics.ARTICLES, "proposals", "article-visit",
                                    ]}
                                />

                            </TableContainer.Table>
                        </TableContainer>
                    </SettingsContainer.Setting>
                </SettingsContainer.Section>

                {element}

            </SettingsContainer>
        </div>
    );
}
